import { Padding } from '@mui/icons-material';
import { createTheme } from '@mui/material/styles';
import { ScaleEnum } from '../enums/enumRegistry';

// Custom breakpoints
const breakpoints = {
  xl: 1440,
  xxl: 1920,
};

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: breakpoints.xl,
      xxl: breakpoints.xxl,
    },
  },
  palette: {
    primary: {
      main: 'rgb(37 99 235)', 
      
    },
    secondary: {
      //main: 'rgba(31, 41, 55)', 
      main: '#0f172a',
    },
    background: {
      default: '#ffffff',
      light: '#F3EDF7',
      grey: '#F6F6F6',
      paper: '#ffffff',
      dissabled: '#f8f8f8',
      border: 'rgb(186, 184, 184)',
    },
    text: {
      primary: '#000000',
      secondary: '#7B809A',
      light: '#ffffff',
      dark:'#344767',
      outlined: '#79747E',
      darkgrey: '#3B3B3B',
    },
    customColors: {
      light: '#ffffff',  
      dark: '#000000',  
    },
    customSidebar: {
      slate900: 'rgb(15 23 42)', // bg-slate-900
      slate800: '#1e293b', // bg-slate-800
      slate700: '#334155', // border-slate-700
      slate600: 'rgb(71 85 105)', // text-slate-600
      blue600: '#2563eb',  // bg-blue-600
      blue700: '#1d4ed8',  // bg-blue-700
      blue300: '#93c5fd',  // border-blue-300
      blue100: '#e0f2fe',  // bg-blue-100
      blue800: '#1e40af',  // bg-blue-800
      blue900: '#1e3a8a',  // bg-blue-900
      green500: '#22c55e', // bg-green-500
      green600: '#16a34a', // bg-green-600
      slate400: '#94a3b8', // text-slate-400
      slate300: '#cbd5e1', // border-slate-300
      slate50: '#f8fafc',  // hover:bg-slate-50
      white: '#ffffff',    // text-white
    },
    customColor: { 
      blue: {
        50: 'rgb(239, 246, 255)',
        100: 'rgb(219, 234, 254)',
        200: 'rgb(191, 219, 254)',
        300: 'rgb(147, 197, 253)',
        400: 'rgb(96, 165, 250)',
        500: 'rgb(59, 130, 246)',
        600: 'rgb(37, 99, 235)',
        700: 'rgb(29, 78, 216)',
        800: 'rgb(30, 64, 175)',
        900: 'rgb(30, 58, 138)',
      },
      green: {
        50: 'rgb(240, 253, 244)',
        100: 'rgb(220, 252, 231)',
        200: 'rgb(187, 247, 208)',
        300: 'rgb(134, 239, 172)',
        400: 'rgb(74, 222, 128)',
        500: 'rgb(34, 197, 94)',
        600: 'rgb(22, 163, 74)',
        700: 'rgb(21, 128, 61)',
        800: 'rgb(22, 101, 52)',
        900: 'rgb(20, 83, 45)',
      },
      slate: {
        50: 'rgb(248, 250, 252)',
        100: 'rgb(241, 245, 249)',
        200: 'rgb(226, 232, 240)',
        300: 'rgb(203, 213, 225)',
        400: 'rgb(148, 163, 184)',
        500: 'rgb(100, 116, 139)',
        600: 'rgb(71, 85, 105)',
        700: 'rgb(51, 65, 85)',
        800: 'rgb(30, 41, 59)',
        900: 'rgb(15, 23, 42)',
      },
      textSlate: {
        50: 'rgb(248, 250, 252)',
        100: 'rgb(241, 245, 249)',
        200: 'rgb(226, 232, 240)',
        300: 'rgb(203, 213, 225)',
        400: 'rgb(148, 163, 184)',
        500: 'rgb(100, 116, 139)',
        600: 'rgb(71, 85, 105)',
        700: 'rgb(51, 65, 85)',
        800: 'rgb(30, 41, 59)',
        900: 'rgb(15, 23, 42)',
      },
      text: {
        color000000DE: '#000000DE',
      },
    },
    customText: {
      xs: {
        fontSize: '0.75rem', // 12px
        lineHeight: '1rem',  // 16px
      },
      sm: {
        fontSize: '0.875rem', // 14px
        lineHeight: '1.25rem', // 20px
      },
      base: {
        fontSize: '1rem',     // 16px
        lineHeight: '1.5rem',  // 24px
      },
      lg: {
        fontSize: '1.125rem', // 18px
        lineHeight: '1.75rem', // 28px
      },
      xl: {
        fontSize: '1.25rem',  // 20px
        lineHeight: '1.75rem', // 28px
      },
      '2xl': {
        fontSize: '1.5rem',   // 24px
        lineHeight: '2rem',    // 32px
      },
      '3xl': {
        fontSize: '1.875rem', // 30px
        lineHeight: '2.25rem', // 36px
      },
    },
    customStyle: {
      shadow: {
        sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        strong: '0px 8px 20px rgba(0, 0, 0, 0.3)',
      },
    },
    treeView: {
      bgGradientExpanded: 'linear-gradient(90deg, #eff6ff 0%, #f5f3ff 100%)',
      bgHover: '#f8fafc',
      bgDefault: '#fff',
      bgModuleIconExpanded: '#dbeafe',
      bgModuleIcon: '#f1f5f9',
      textModuleExpanded: '#1e293b',
      textModule: '#334155',
      textModuleHover: '#0f172a',
      borderModuleExpanded: '#bfdbfe',
      borderModule: 'transparent',
      shadowModuleExpanded: '0 1px 2px 0 rgb(16 112 255 / 0.08)',
      shadowModuleHover: '0 1px 2px 0 rgb(16 112 255 / 0.06)',
      badgeBgExpanded: '#dbeafe',
      badgeBg: '#f1f5f9',
      badgeTextExpanded: '#2563eb',
      badgeText: '#64748b',
      badgeBorderExpanded: '#bfdbfe',
      dot: '#60a5fa',
      childBgHover: '#dbeafe',
      childBg: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
      childText: '#334155',
      childTextHover: '#0f172a',
      childBadgeBg: '#f1f5f9',
      childBadgeBgHover: '#dbeafe',
      childBadgeText: '#64748b',
      childBadgeTextHover: '#2563eb',
      childIconBgHover: 'linear-gradient(135deg, #dbeafe 0%, #f0f9ff 100%)',
      childIconBg: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
      childIconText: '#64748b',
      childIconTextHover: '#2563eb',
      line: '#bfdbfe',
    },
    
    customSizes: {
      icon: {
        small: '16px',
      },
      cover: {
        small: '20px',
        medium: '32px',
        large: '40px',
      },
      borderRadius: {
        default: '8px',
        small: '4px',
        medium: '6px',
      },
    },

  },
  typography: {
    // fontFamily: 'GeistSans, GeistSans Fallback',
   // fontFamily: `ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"`,
    fontFamily: `Inter, Roboto, Helvetica, Arial, sans-serif`,
    
    bodyMedium: {
      fontWeight: 500,
    },
    bodySemiBold: {
      fontWeight: 600,
    },
    body1: {
      fontSize: '1rem',           
      fontWeight: 'normal',       
    },
    body2: {
      fontSize: '0.875rem',      
      fontWeight: 'normal',       
    },
    h1: {
      fontSize: '2.5rem',         
      fontWeight: 'bold',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 'bold',
    },
    h5: {
      display: 'flex',
      alignItems: 'center',
    },
    h5Center: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      color: '#2c3e50',
    },
    heading1: {
      fontSize: '18px',
      fontWeight: '600',
      lineHeight: '24px',
      [`@media (min-width:${breakpoints.xl}px)`]: {  
        fontSize: "22px",
        lineHeight: "28px",
      },
    },
    heading2: {
      fontSize: '14px',
      fontWeight: '500',
      lineHeight: '20px',
      [`@media (min-width:${breakpoints.xl}px)`]: {  
        fontSize: "18px",
        lineHeight: "24px",
      },
    },
    heading3: {
      fontSize: '12px',
      fontWeight: '400',
      lineHeight: '16px',
      [`@media (min-width:${breakpoints.xl}px)`]: {  
        fontSize: "16px",
        lineHeight: "20px",
      },
    },
    heading4: {
      fontSize: '10px',
      fontWeight: '300',
      lineHeight: '14px',
      [`@media (min-width:${breakpoints.xl}px)`]: {  
        fontSize: "14px",
        lineHeight: "18px",
      },
    },
    text1: {
      fontSize: '14px',
      fontWeight: '500',
      lineHeight: '20px',
      [`@media (min-width:${breakpoints.xl}px)`]: {  
        fontSize: "18px",
        lineHeight: "24px",
      },
    },
    text2: {
      fontSize: '12px',
      fontWeight: '400',
      lineHeight: '16px',
      [`@media (min-width:${breakpoints.xl}px)`]: {  
        fontSize: "16px",
        lineHeight: "20px",
      },
    },
    text2SemiBold: {
      fontSize: '12px',
      fontWeight: '500',
      lineHeight: '16px',
      [`@media (min-width:${breakpoints.xl}px)`]: {  
        fontSize: "14px",
        lineHeight: "20px",
      },
    },
    text3: {
      fontSize: '11px',
      fontWeight: '400',
      lineHeight: '14px',
      [`@media (min-width:${breakpoints.xl}px)`]: {  
        fontSize: "13px",
        lineHeight: "18px",
      },
    },
    title: {
      fontSize: '24px',
      lineHeight: '32px', 
      fontWeight: 600,
      [`@media (min-width:${breakpoints.xl}px)`]: {  
        fontSize: "30px",
        lineHeight: "42px",
        //fontWeight: 700,
      },
    },
    context: {
      fontSize: '14px',
      lineHeight: '20px',
      fontWeight: 400,
      [`@media (min-width:${breakpoints.xl}px)`]: {  
        fontSize: "15px",
        lineHeight: "24px",
      },
    },
    contentSmall: {
      fontSize: '11px',
      lineHeight: '16px',
      fontWeight: 400,
      [`@media (min-width:${breakpoints.xl}px)`]: {  
        fontSize: "13px",
        lineHeight: "19px",
      },
    },
    contentSmallSemiBold: {
      fontSize: '9px',
      lineHeight: '12px',
      fontWeight: 500,
      [`@media (min-width:${breakpoints.xl}px)`]: {  
        fontSize: "11px",
        lineHeight: "16px",
      },
    },
    breadcrumb: {
      fontSize: '11px',
      lineHeight: '15px',
      fontWeight: 500,
      [`@media (min-width:${breakpoints.xl}px)`]: {  
        fontSize: "15px",
        lineHeight: "21px",
      },
    },
    sidebar: {
      fontSize: '13px',
      lineHeight: '20px',
      fontWeight: 400,
      [`@media (min-width:${breakpoints.xl}px)`]: {  
        fontSize: "16px",
        lineHeight: "28px",
      },
    },
    sidebarSubmenu: {
      fontSize: '11.5px',
      lineHeight: '16px',
      fontWeight: 400,
      [`@media (min-width:${breakpoints.xl}px)`]: {  
        fontSize: "14px",
        lineHeight: "20px",
      },
    },
    sidebarLogoText: {
      fontSize: '18px',
      lineHeight: '34px',
      fontWeight: 800,
      letterSpacing: '0',
      height: '32px',
      [`@media (min-width:${breakpoints.xl}px)`]: {  
        fontSize: "24px",
        lineHeight: "52px",
        height: "52px",
      },
    },
    componentTitle: {
      fontSize: '14px',
      lineHeight: '18px',
      fontWeight: 500,
      [`@media (min-width:${breakpoints.xl}px)`]: {  
        fontSize: "18px",
        lineHeight: "24px",
      },
    },
    componentTitleSmall : {
      fontSize: '13px',
      lineHeight: '18px',
      fontWeight: 500,
      [`@media (min-width:${breakpoints.xl}px)`]: {  
        fontSize: "17px",
        lineHeight: "22px",
      },
    },
    tableHeading: {
      fontSize: '12px',
      lineHeight: '16px',  
      fontWeight: 600,
      [`@media (min-width:${breakpoints.xl}px)`]: {  
        fontSize: "16px",
        lineHeight: "20px",
      },
    },
    tableCell: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
    },
  },
  components: {
    MuiTypography: {
      variants: [
        {
          props: { variant: 'h5-center' },
          style: {
            fontSize: '1.5rem',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            color: '#2c3e50',
            marginBottom: '0rem',
          },
        },
        {
          props: { variant: 'title' },
          style: ({ theme }) => theme.typography.title,
        },
        {
          props: { variant: 'context' },
          style: ({ theme }) => theme.typography.context,
        },
        {
          props: { variant: 'contentSmall' },
          style: ({ theme }) => theme.typography.contentSmall,
        },  
        {
          props: { variant: 'contentSmallSemiBold' },
          style: ({ theme }) => theme.typography.contentSmallSemiBold,
        },
        {
          props: { variant: 'breadcrumb' },
          style: ({ theme }) => theme.typography.breadcrumb,
        },
        {
          props: { variant: 'sidebar' },
          style: ({ theme }) => theme.typography.sidebar,
        },
                 {
           props: { variant: 'sidebarSubmenu' },
           style: ({ theme }) => theme.typography.sidebarSubmenu,
         },
         {
           props: { variant: 'sidebarLogoText' },
           style: ({ theme }) => theme.typography.sidebarLogoText,
         },
        {
          props: { variant: 'componentTitle' },
          style: ({ theme }) => theme.typography.componentTitle,
        },
        {
          props: { variant: 'tableHeading' },
          style: ({ theme }) => theme.typography.tableHeading,
        },
        {
          props: { variant: 'tableCell' },
          style: ({ theme }) => theme.typography.tableCell,
        },
        {
          props: { variant: 'sidebarLogoText' },
          style: ({ theme }) => theme.typography.sidebarLogoText,
        }
      ],
      // styleOverrides: {
      //   root: {
      //     fontSize: '12px',
      //   },
      // },
    },
    MuiFormLabel: {
      styleOverrides: {
        // root: {
        //   fontSize: '12px',
        // },
        asterisk: {
          color: 'red',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '[class*="container"]': {
          display: 'normal',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: 'linear-gradient(to right, #130621, #2a2b2f)',
          margin: 100,
        },
      },
      
    },
    MuiTextField: {
      defaultProps: {
        minWidth: "50px",
        slotProps: {
          inputLabel: { shrink: true },
        },
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            //padding: '0px',
            borderRadius: '4px',
            '&:hover': {
              //boxShadow: '0 6px 10px rgba(0, 0, 0, 0.15)',
            },
            '&.Mui-focused': {
              //boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({theme}) => ({
          size: 'small',
          minWidth: '100px',
          color: theme.palette.primary.main,
          // borderRadius: '16px',
          borderRadius: '6px',
          textTransform: 'none',
          '&:disabled': {
              backgroundColor: '#E5E4E2',
              color: '#899499',
          },
          '&:hover': {
            boxShadow: '0 3px 5px rgba(0, 0, 0, 0.15)',
          },
          '&:active': {
          },
        }),
      },
      variants: [
        {
          props: { variant: 'true' }, 
          style: ({theme}) => ({
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.text.light,
            size: 'small',
            textTransform: 'none',
          }),
        },
        {
          props: { variant: 'false' }, 
          style: ({theme}) => ({
            backgroundColor: 'transparent', 
            border: `1px solid ${theme.palette.primary.main}`,
            color: theme.palette.primary.main,
            size: 'small',
            textTransform: 'none',
          }),
        },
        {
          props: { variant: 'primary-button' },
          style: {
            background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)',
            color: '#fff',
            width: 'auto',
            minWidth: '60px',
            padding: '4px 12px !important',
            borderRadius: '6px', 
            fontSize: '12px',
            fontWeight: '400 !important',
            textTransform: 'none',
            transition: 'all 0.2s',
            '&:hover': {
              background: 'linear-gradient(90deg, #2563eb 0%, #1d4ed8 100%)',
              boxShadow: '0 0.25rem 1rem 0 rgba(30, 64, 175, 0.15)',
              transform: 'translateY(-0.125rem)',
            },

            [`@media (min-width:${breakpoints.xl}px)`]: {  
              fontSize: "17px",
              lineHeight: "20px",
              borderRadius: '8px',
              padding: "8px 14px!important",
            },
          }
        },
        {
          props: { variant: 'primaryBtn' },
          style: {
            background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)',
            color: '#fff',
            fontWeight: 500,
            paddingTop: '0.5rem',
            paddingBottom: '0.5rem',
            paddingLeft: '1rem',
            paddingRight: '1rem',
            borderRadius: '0.5rem',
            fontSize: '0.8rem',
            boxShadow: '0 0.125rem 0.5rem 0 rgba(30, 64, 175, 0.10)',
            textTransform: 'none',
            minHeight: '2rem',
            width: '100%',
            transition: 'all 0.2s',
            lineHeight: '1.5rem',
            '&:hover': {
              background: 'linear-gradient(90deg, #2563eb 0%, #1d4ed8 100%)',
              boxShadow: '0 0.25rem 1rem 0 rgba(30, 64, 175, 0.15)',
              transform: 'translateY(-0.125rem)',
            },

            [`@media (min-width:${breakpoints.xl}px)`]: {  
              fontSize: "16px",
              lineHeight: "16px",
              paddingTop: "0.75rem",
              paddingBottom: "0.75rem",
              paddingLeft: "1rem",
              paddingRight: "1rem",
              minHeight: "3rem",
            },
          },
        },
        {
          props: { variant: 'sectionButton' },
          style: {
            color: '#64748b',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '4px 12px',
            background: '#fff',
            fontSize: 14,
            textTransform: 'none',
            '&:disabled': {
              color: '#64748b',
              border: '1px solid #e2e8f0',
              background: 'rgba(221, 214, 214, 0.32)',
              cursor: 'not-allowed',
              opacity: 0.6,
            },
          },
        },
        {
          props: { variant: 'outlineButton' },
          style: {
            padding: '4px 8px',
            lineHeight: '20px',
            fontSize: '13px',
            fontWeight: 400,
            textTransform: 'none',
            borderRadius: '4px',
            backgroundColor: 'transparent',
            border: '1px solid',
            borderColor: 'rgba(0, 0, 0, 0.23)',
            [`@media (min-width:${breakpoints.xl}px)`]: {  
              fontSize: "15px",
              lineHeight: "28px",
              padding: "6px 8px",
            },
          },
        },
      ],
    },
    MuiSelect: {
      styleOverrides: {
        root: ({theme}) => ({
          minWidth: '100%',  
          '&:disabled': {
            backgroundColor: theme.palette.background.dissabled,
          },
        }),
        select: {
          whiteSpace: 'nowrap',  // Truncate text when it's too long
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '100%',
        },
        outlined: ({theme}) => ({
          '&:disabled': {
            backgroundColor: theme.palette.background.dissabled,
          },
          '&:hover': {
            // Optionally add hover effect (e.g., boxShadow)
          },
          '&.Mui-focused': {
            color: theme.palette.text.disabled,
          },
        }),
      },
   
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          maxHeight: '300px', // Set the maxHeight for the dropdown menu
          overflow: 'auto',    // Enable scroll when content exceeds maxHeight
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            fontWeight: 'bold',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          fontSize: "12px",
          [`@media (min-width:${breakpoints.xl}px)`]: {
            fontSize: "15px",
          },
        },
        root: ({ theme }) => ({
          borderRadius: "8px",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.text.border,
            color: theme.palette.text.outlined,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.text.border,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.text.border,
            color: theme.palette.text.outlined,
          },
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: "12px",
          transform: "translate(14px, 9px) scale(1)", // resting position
          color: theme.palette.text.outlined,
          [`@media (min-width:${breakpoints.xl}px)`]: {
            fontSize: "15px",
          },
          "&.Mui-focused": {
            color: theme.palette.text.outlined,
          },
        }),
        shrink: {
          fontSize: "12px",
          transform: "translate(14px, -6px) scale(0.85)", // floating position
          [`@media (min-width:${breakpoints.xl}px)`]: {
            fontSize: "15px",
            transform: "translate(14px, -6px) scale(0.85)",
          },
        },
      },
    },
    MuiSvgIcon: {
      defaultProps: {
        fontSize: 'small',
      },
      styleOverrides: {
        root: {

        },
      },
    },    
    
    MuiCheckbox: {
      styleOverrides: {
        root: {
          borderRadius: '50%',
          '& svg': {
            fontSize: 17,
          },
        },
      },
    },
    MuiDataGrid: {
      defaultProps: {
        slotProps: {
          loadingOverlay: {
            variant: 'linear-progress',
            noRowsVariant: 'linear-progress',
          },
          columnsPanel: {
            sx: {
              maxHeight: '44vh',
              overflowY: 'auto',
              '& .MuiButton-root': {
                padding: '4px 15px',
                minWidth: 'auto',
              },
              '& .MuiOutlinedInput-root': {
                height: '42px',
              }
            }
          }
        },
      },
      styleOverrides: {
        root:({ theme }) => ({
          border: 'none',
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            borderBottom: 'none',
          },
          '& .MuiDataGrid-columnHeaders': {
            borderBottom: '1px solid rgba(224, 224, 224, 0.41)',
          },
          '& .row-disabled': {
            backgroundColor: '#f5f5f5',
            opacity: 0.5,
            pointerEvents: 'none',
          },
          '& .row-selected': {
            backgroundColor: '#F0F0F0',
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
            boxShadow: 'none',
          },
          '& .MuiDataGrid-cell:focus-within': {
            outline: 'none',
            boxShadow: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus': {
            outline: 'none',
            boxShadow: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none',
            boxShadow: 'none',
          },
          '& .MuiDataGrid-columnHeaderCheckbox': {
            outline: 'none',
            boxShadow: 'none',
          },
          '& .MuiDataGrid-scrollbarFiller': {
            backgroundColor: theme.palette.background.grey,
          },
        }),
        filterFormValueInput: {
           "& .MuiOutlinedInput-input": {
             borderRadius:"8px"
            },
        },
        // columnHeaderTitleContainer: ({theme}) => ({
        //   flex: '1 1 0%',
        // }),
        columnHeaderTitle: ({theme}) => ({
          fontSize: '11px',
          lineHeight: '16px',  
          fontWeight: 600,
          color: theme.palette.customColor.text.color000000DE,
          [`@media (min-width:${breakpoints.xl}px)`]: {  
            fontSize: "14px",
            lineHeight: "20px",
          },
        }),
        columnHeader: ({theme}) => ({ 
          backgroundColor: 'rgb(249, 250, 251)',
          paddingTop: 10,
          paddingBottom: 10,
        
          "& .MuiDataGrid-menuIconButton": {
            width: "16px",
            height: "16px",
            "& .MuiSvgIcon-root": { fontSize: "12px", },
            [`@media (min-width:${breakpoints.xl}px)`]: {  
              width: "20px",
              height: "20px",
              "& .MuiSvgIcon-root": { fontSize: "16px", },
            },
          },
          "& .MuiDataGrid-sortButton": {
            width: "16px",
            height: "16px",
            "& .MuiSvgIcon-root": { fontSize: "12px", },
            [`@media (min-width:${breakpoints.xl}px)`]: {  
              width: "20px",
              height: "20px",
              "& .MuiSvgIcon-root": { fontSize: "16px", },
            },
          },
          "& .MuiButtonBase-root": {
            width: "16px",
            height: "16px",
            "& .MuiDataGrid-filterIcon": { fontSize: "12px" },
            [`@media (min-width:${breakpoints.xl}px)`]: {  
              width: "20px",
              height: "20px",
              "& .MuiDataGrid-filterIcon": { fontSize: "16px", },
            },
          },
        

          '&[data-field="__check__"]': { 
            paddingLeft: '0px',
            paddingRight: '0px',
          },
        }),
        cell: ({theme}) => ({
          fontSize: '11px',
          lineHeight: '15px',  
          fontWeight: 400,
          paddingTop: 2,
          paddingBottom: 2,
          color: '#4F5154',
          '&[data-field="__check__"]': { 
            paddingLeft: '0px',
            paddingRight: '0px',
          },
          [`@media (min-width:${breakpoints.xl}px)`]: {  
            fontSize: "13px",
            lineHeight: "18px",
            paddingTop: 6,
            paddingBottom: 6,
          },
        }),
    },
  },
    MuiChip: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: ({theme}) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.text.light,
        }),
      },
      variants: [
        {
          props: { variant: 'dataGridChip' },
          style: ({ theme }) => ({
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f1f5f9",
            color: "#475569",
            padding: "0px 6px",
            borderRadius: "9999px",
            fontSize: '10px',
            lineHeight: '15px',
            fontWeight: 500,
            height: "max-content",
            width: "max-content",
            "& .MuiChip-label": {
              padding: 0,
            },
            [`@media (min-width:${breakpoints.xl}px)`]: {  
              fontSize: "12px",
              lineHeight: "16px",
              padding: "0px 4px",

            },
          }),
        },
        {
          props: { variant: 'tagChip' },
          style: ({ theme }) => ({
            fontSize: '10px',
            lineHeight: '15px',
            fontWeight: 500,
          }),
        }
      ],
    },
    MuiTabs: {
      styleOverrides: {
        indicator: ({theme}) => ({
          height: '3px',
          borderRadius: '4px 4px 0 0',
        }),
      },
    },
    MuiTab: {   
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'none',
          fontSize: '0.95rem',
          fontWeight: 500,
          color: theme.palette.text.primary, // normal text color
          '&.Mui-selected': {
            fontWeight: 600,
            borderRadius: '4px 4px 0 0',
          },
        }),
      },
    },

    MuiIconButton: {
      variants: [
        {
          props: { variant: 'icon-cover' },
          style: ({ theme }) => ({
            width: theme.palette.customSizes.cover.medium,
            height: theme.palette.customSizes.cover.medium,
            borderRadius: theme.palette.customSizes.borderRadius.medium,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0px',
            [`@media (min-width:${breakpoints.xl}px)`]: {  
              width: theme.palette.customSizes.cover.large,
              height: theme.palette.customSizes.cover.large,
            },
          }),
        },
        {
          props: { variant: 'icon-cover-small' },
          style: ({ theme }) => ({
            width: theme.palette.customSizes.cover.small,
            height: theme.palette.customSizes.cover.small,
            borderRadius: theme.palette.customSizes.borderRadius.small,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0px',
            [`@media (min-width:${breakpoints.xl}px)`]: {  
              width: '28px',
              height: '28px',
              borderRadius: theme.palette.customSizes.borderRadius.medium,
            },
          }),
        },
      ],
    },
    
    MuiBox: {
      variants: [
        {
          props: { variant: 'icon-cover' },
          style: ({ theme }) => ({
            width: theme.palette.customSizes.cover.medium,
            height: theme.palette.customSizes.cover.medium,
            borderRadius: theme.palette.customSizes.borderRadius.default,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0px',
          }),
        },
        {
          props: { variant: 'cover-32' },
          style: ({ theme }) => ({
            width: theme.palette.customSizes.cover.medium,
            height: theme.palette.customSizes.cover.medium,
            borderRadius: theme.palette.customSizes.borderRadius.default,
            overflow: 'hidden',
          }),
        },

      ],
    },
    
  },
});

export default theme;
export { breakpoints };
